import { useEffect, useState } from "react";
import { Eye, Printer, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY, ORDER_STATUS_COLORS } from "../../lib/format";

export default function OrdersPage() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [invoicePending, setInvoicePending] = useState(false);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [cargo, setCargo] = useState({ tracking: "", company: "" });
  const [selected, setSelected] = useState([]);

  const load = () => {
    const params = new URLSearchParams({ page: String(page), limit: "30" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (payStatus) params.set("payment_status", payStatus);
    if (channel) params.set("channel", channel);
    if (invoicePending) params.set("invoice_pending", "true");
    api.get(`/admin/orders?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [q, status, payStatus, channel, invoicePending, page]);
  useEffect(() => { api.get("/admin/orders/summary").then(({ data }) => setSummary(data)).catch(() => {}); }, [data?.total]);

  const CHIPS = [
    { key: "all", label: "Tümü", count: summary?.total, apply: () => { setStatus(""); setPayStatus(""); setInvoicePending(false); } },
    { key: "new", label: "Yeni Siparişler", count: summary?.new_orders, apply: () => { setStatus("Ödeme Başarılı"); setInvoicePending(false); } },
    { key: "prep", label: "Hazırlanıyor", count: summary?.preparing, apply: () => { setStatus("Hazırlanıyor"); setInvoicePending(false); } },
    { key: "ready", label: "Kargoya Verilecek", count: summary?.ready_to_ship, apply: () => { setStatus("Kargoya Hazır"); setInvoicePending(false); } },
    { key: "shipped", label: "Kargoda", count: summary?.shipped, apply: () => { setStatus("Kargoya Verildi"); setInvoicePending(false); } },
    { key: "delivered", label: "Teslim Edildi", count: summary?.delivered, apply: () => { setStatus("Teslim Edildi"); setInvoicePending(false); } },
    { key: "cancelled", label: "İptal", count: summary?.cancelled, apply: () => { setStatus("İptal Edildi"); setInvoicePending(false); } },
    { key: "returned", label: "İade", count: summary?.returned, apply: () => { setStatus("İade Edildi,İade Talebi"); setInvoicePending(false); } },
    { key: "paywait", label: "Ödeme Bekliyor", count: summary?.payment_pending, apply: () => { setStatus("Ödeme Bekliyor"); setInvoicePending(false); } },
    { key: "payprocessing", label: "Ödeme İşleniyor", count: summary?.by_status?.["Ödeme İşleniyor"], apply: () => { setStatus("Ödeme İşleniyor"); setInvoicePending(false); } },
    { key: "payfailed", label: "Ödeme Başarısız", count: (summary?.by_status?.["Ödeme Başarısız"] || 0) + (summary?.by_status?.["Ödeme Süresi Doldu"] || 0) + (summary?.by_status?.["Ödeme İptal"] || 0), apply: () => { setStatus("Ödeme Başarısız,Ödeme Süresi Doldu,Ödeme İptal"); setInvoicePending(false); } },
    { key: "invwait", label: "Fatura Bekliyor", count: summary?.invoice_pending, apply: () => { setStatus(""); setInvoicePending(true); } },
    { key: "problem", label: "Sorunlu", count: summary?.problem, apply: () => { setStatus("Sorunlu,Ödeme Başarısız"); setInvoicePending(false); } },
  ];
  const [activeChip, setActiveChip] = useState("all");
  const pickChip = (chip) => { setActiveChip(chip.key); chip.apply(); setPage(1); };

  const toggleSelect = (no) => setSelected((s) => s.includes(no) ? s.filter((x) => x !== no) : [...s, no]);

  const DELETABLE = ["Taslak", "Ödeme Bekliyor", "Ödeme İşleniyor", "Ödeme Başarısız", "Ödeme Süresi Doldu", "Ödeme İptal", "İptal Edildi"];
  const canDelete = (o) => o && o.payment_status !== "Başarılı" && DELETABLE.includes(o.status);

  const deleteOrder = async (orderNumber) => {
    if (!window.confirm("Bu ödeme alınmamış siparişi silmek istediğinize emin misiniz? Varsa stok rezervasyonu serbest bırakılır. Bu işlem geri alınamaz.")) return;
    try {
      await api.delete(`/admin/orders/${orderNumber}`);
      toast.success("Sipariş silindi");
      setDetail(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulkAction = async (action) => {
    const labels = { mark_preparing: "Hazırlanıyor", mark_ready: "Kargoya Hazır", mark_shipped: "Kargoya Verildi" };
    if (!window.confirm(`${selected.length} sipariş "${labels[action]}" yapılacak. Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post("/admin/orders/bulk", { order_numbers: selected, action });
      toast.success(`${data.ok_count} sipariş güncellendi${data.failed_count ? `, ${data.failed_count} başarısız` : ""}`);
      if (data.failed_count) toast.warning(data.failed.map((f) => `${f.order_number}: ${f.error}`).join(" | "));
      setSelected([]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (orderNumber) => {
    const { data } = await api.get(`/admin/orders/${orderNumber}`);
    setDetail(data);
    setCargo({ tracking: data.cargo_tracking_number || "", company: data.cargo_company || "" });
    setRefundAmount("");
    api.get(`/admin/orders/${orderNumber}/payment`).then(({ data }) => setPaymentInfo(data)).catch(() => setPaymentInfo(null));
  };

  const updateStatus = async (orderNumber, updates) => {
    try {
      const { data } = await api.put(`/admin/orders/${orderNumber}/status`, updates);
      setDetail(data);
      toast.success("Sipariş güncellendi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const selectCls = "h-10 px-3 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5" data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Siparişler <span className="text-sm text-neutral-500 font-normal">({data?.total ?? 0})</span></h1>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Sipariş no, müşteri..."
              className="h-10 pl-9 pr-4 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white w-56 focus:outline-none focus:border-blue-500" data-testid="admin-order-search" />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={selectCls} data-testid="admin-order-status-filter">
            <option value="">Tüm Durumlar</option>
            {data?.statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={payStatus} onChange={(e) => { setPayStatus(e.target.value); setPage(1); }} className={selectCls} data-testid="admin-order-payment-filter">
            <option value="">Ödeme (Tümü)</option>
            {data?.payment_statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={channel} onChange={(e) => { setChannel(e.target.value); setPage(1); }} className={selectCls} data-testid="admin-order-channel-filter">
            <option value="">Tüm Siparişler (Kaynak)</option>
            <option value="BIGELDI">Bigeldi</option>
            <option value="TRENDYOL">Trendyol</option>
            <option value="HEPSIBURADA">Hepsiburada</option>
            <option value="PTTAVM">PTTAVM</option>
            <option value="AMAZON">Amazon</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="order-filter-chips">
        {CHIPS.map((chip) => (
          <button key={chip.key} onClick={() => pickChip(chip)}
            className={`h-9 px-3.5 rounded-xl text-xs font-semibold transition-colors ${activeChip === chip.key ? "bg-blue-600 text-white" : "bg-[#181B20] text-neutral-400 border border-[#272B33] hover:text-white"}`}
            data-testid={`order-chip-${chip.key}`}>
            {chip.label}{chip.count != null && <span className="ml-1.5 opacity-70">({chip.count})</span>}
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="bg-blue-600/10 border border-blue-500/30 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
          <p className="text-sm text-blue-300 font-semibold">{selected.length} sipariş seçildi</p>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => bulkAction("mark_preparing")} className="h-9 px-4 rounded-xl bg-indigo-600/20 text-indigo-300 text-xs font-semibold hover:bg-indigo-600/30" data-testid="bulk-preparing">Hazırlanıyor Yap</button>
            <button onClick={() => bulkAction("mark_ready")} className="h-9 px-4 rounded-xl bg-cyan-600/20 text-cyan-300 text-xs font-semibold hover:bg-cyan-600/30" data-testid="bulk-ready">Kargoya Hazır Yap</button>
            <button onClick={() => bulkAction("mark_shipped")} className="h-9 px-4 rounded-xl bg-teal-600/20 text-teal-300 text-xs font-semibold hover:bg-teal-600/30" data-testid="bulk-shipped">Kargoya Verildi Yap</button>
            <button onClick={() => setSelected([])} className="h-9 px-3 rounded-xl text-neutral-400 text-xs hover:text-white">Vazgeç</button>
          </div>
        </div>
      )}

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="admin-orders-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
              <th className="px-4 py-3.5 w-8">
                <input type="checkbox" data-testid="order-select-all"
                  checked={data?.items?.length > 0 && selected.length === data.items.length}
                  onChange={(e) => setSelected(e.target.checked ? data.items.map((o) => o.order_number) : [])}
                  className="accent-blue-500" />
              </th>
              <th className="px-5 py-3.5">Sipariş No</th><th className="px-4 py-3.5">Kaynak</th><th className="px-4 py-3.5">Müşteri</th><th className="px-4 py-3.5">Tarih</th>
              <th className="px-4 py-3.5">Durum</th><th className="px-4 py-3.5">Ödeme</th><th className="px-4 py-3.5">Tutar</th><th className="px-4 py-3.5 text-right">Detay</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((o) => (
              <tr key={o.order_id} className="border-b border-[#272B33]/50 hover:bg-[#1E222A]/50" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.includes(o.order_number)} onChange={() => toggleSelect(o.order_number)}
                    className="accent-blue-500" data-testid={`order-select-${o.order_number}`} />
                </td>
                <td className="px-5 py-3 font-mono-ed text-white">{o.order_number}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${(o.channel || "BIGELDI") === "BIGELDI" ? "bg-amber-500/15 text-amber-400" : "bg-purple-500/15 text-purple-400"}`}
                    data-testid={`order-channel-${o.order_number}`}>
                    {({ BIGELDI: "Bigeldi", TRENDYOL: "Trendyol", HEPSIBURADA: "Hepsiburada", PTTAVM: "PTTAVM", AMAZON: "Amazon" })[o.channel || "BIGELDI"] || o.channel}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-white">{o.contact?.name}</p>
                  <p className="text-[11px] text-neutral-500">{o.contact?.email}{o.guest_email ? " · misafir" : ""}</p>
                </td>
                <td className="px-4 py-3 text-neutral-400 text-xs">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${ORDER_STATUS_COLORS[o.status] || "bg-neutral-700 text-neutral-300"}`} data-testid="admin-order-status-badge">{o.status}</span></td>
                <td className="px-4 py-3 text-xs text-neutral-400">{o.payment_status}</td>
                <td className="px-4 py-3 font-semibold text-white">{fmtTRY(o.total)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openDetail(o.order_number)} className="p-2 rounded-lg text-neutral-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid="admin-order-view"><Eye size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items?.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Sipariş bulunamadı</p>}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50" data-testid="order-detail-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail(null)} />
          <div className="absolute right-0 inset-y-0 w-full max-w-xl bg-[#13161B] border-l border-[#272B33] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-mono-ed font-bold text-xl text-white">{detail.order_number}</h2>
                <p className="text-xs text-neutral-500">{fmtDate(detail.created_at)} · Ödeme: {detail.payment_status} ({detail.payment_provider || "—"})</p>
              </div>
              <div className="flex items-center gap-1">
                {canDelete(detail) && (
                  <button onClick={() => deleteOrder(detail.order_number)} className="h-9 px-3 rounded-lg text-xs font-semibold text-rose-400 hover:bg-rose-500/10 flex items-center gap-1.5" data-testid="order-delete-btn">
                    <Trash2 size={15} /> Sil
                  </button>
                )}
                <button onClick={() => window.print()} className="p-2 text-neutral-400 hover:text-white" title="Yazdır" data-testid="order-print-btn"><Printer size={18} /></button>
                <button onClick={() => setDetail(null)} className="p-2 text-neutral-400 hover:text-white" data-testid="order-detail-close"><X size={20} /></button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="order-op-actions">
              <button onClick={async () => {
                  try { const { data } = await api.get(`/admin/orders/${detail.order_number}/payment`); toast.info(`Ödeme durumu: ${data.payment?.status || "kayıt yok"} (${data.mode})`); openDetail(detail.order_number); }
                  catch (e) { toast.error(errMsg(e)); }
                }}
                className="h-9 px-4 rounded-xl bg-[#181B20] border border-[#272B33] text-xs font-semibold text-neutral-300 hover:text-white" data-testid="order-check-payment">
                Ödemeyi Kontrol Et
              </button>
              {detail.payment_status === "Başarılı" && (
                <button onClick={async () => {
                    try { const { data } = await api.post(`/admin/orders/${detail.order_number}/invoice`); data.ok ? toast.success("Fatura hazır") : toast.error("Fatura oluşturulamadı"); }
                    catch (e) { toast.error(errMsg(e)); }
                  }}
                  className="h-9 px-4 rounded-xl bg-amber-600/15 text-amber-400 text-xs font-semibold hover:bg-amber-600/25" data-testid="order-create-invoice">
                  Fatura Oluştur
                </button>
              )}
            </div>

            <div className="bg-[#181B20] border border-[#272B33] rounded-xl p-4 mb-4 text-sm space-y-1">
              <p className="text-white font-semibold">{detail.contact?.name} · {detail.contact?.phone}</p>
              <p className="text-neutral-400 text-xs">{detail.contact?.email}{detail.guest_email && " · misafir siparişi"}</p>
              <p className="text-neutral-400 text-xs pt-2">Teslimat: {detail.shipping_address?.address_line} {detail.shipping_address?.district}/{detail.shipping_address?.city}</p>
              {detail.shipping_preference && (detail.shipping_preference.carrier || detail.shipping_preference.delivery_note) && (
                <p className="text-xs pt-1 text-cyan-300" data-testid="order-shipping-preference">
                  Kargo tercihi: <b>{detail.shipping_preference.carrier || "—"}</b>
                  {detail.shipping_preference.delivery_note ? ` · ${detail.shipping_preference.delivery_note}` : ""}
                </p>
              )}
              {detail.note && <p className="text-amber-400/80 text-xs">Not: {detail.note}</p>}
            </div>

            <div className="space-y-2 mb-4">
              {detail.items?.map((i) => (
                <div key={i.product_id + (i.variant_id || "")} className="flex items-center gap-3 bg-[#181B20] border border-[#272B33] rounded-xl p-3 text-sm">
                  {i.image && <img src={i.image} alt="" className="w-11 h-13 rounded-lg object-cover" />}
                  <div className="flex-1">
                    <p className="text-white font-medium">{i.name}</p>
                    <p className="text-[11px] text-neutral-500">{i.variant_label ? `${i.variant_label} · ` : ""}{i.sku} × {i.quantity}</p>
                    {i.customization && (
                      <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-2" data-testid="order-item-customization">
                        {i.customization.photo_url && <a href={i.customization.photo_url} target="_blank" rel="noreferrer"><img src={i.customization.photo_url} alt="Kişiselleştirme" className="w-8 h-8 rounded object-cover border border-[#272B33]" /></a>}
                        <span>Kişiselleştirme: {i.customization.text ? `"${i.customization.text}"` : "fotoğraf yüklendi"}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">{fmtTRY(i.line_total)}</p>
                    <p className="text-[10px] text-neutral-500">KDV %{i.vat_rate}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-sm space-y-1.5 mb-6 bg-[#181B20] border border-[#272B33] rounded-xl p-4">
              <div className="flex justify-between text-neutral-400"><span>Ara toplam</span><span>{fmtTRY(detail.subtotal)}</span></div>
              {detail.campaign_discount > 0 && <div className="flex justify-between text-emerald-400"><span>Kampanya</span><span>-{fmtTRY(detail.campaign_discount)}</span></div>}
              {detail.coupon_discount > 0 && <div className="flex justify-between text-emerald-400"><span>Kupon ({detail.coupon_code})</span><span>-{fmtTRY(detail.coupon_discount)}</span></div>}
              <div className="flex justify-between text-neutral-400"><span>Kargo</span><span>{detail.shipping_fee === 0 ? "Ücretsiz" : fmtTRY(detail.shipping_fee)}</span></div>
              <div className="flex justify-between text-neutral-500 text-xs"><span>KDV dahil</span><span>{fmtTRY(detail.vat_total)}</span></div>
              <div className="flex justify-between text-white font-bold text-base pt-2 border-t border-[#272B33]"><span>Toplam</span><span>{fmtTRY(detail.total)}</span></div>
            </div>

            {paymentInfo?.payment && (
              <div className="bg-[#181B20] border border-[#272B33] rounded-xl p-4 mb-4 space-y-2" data-testid="order-payment-panel">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500">Ödeme Bilgileri {paymentInfo.mode === "TEST" && <span className="text-amber-400 font-bold ml-2">TEST MODU</span>}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><p className="text-neutral-500">Sağlayıcı</p><p className="text-white font-medium">{paymentInfo.payment.provider === "garanti" ? "Garanti BBVA" : paymentInfo.payment.provider || "—"}</p></div>
                  <div><p className="text-neutral-500">Durum</p><p className="text-white font-medium" data-testid="order-payment-state">{paymentInfo.payment.status}</p></div>
                  <div><p className="text-neutral-500">Taksit</p><p className="text-white font-medium">{paymentInfo.payment.installment_count > 1 ? `${paymentInfo.payment.installment_count} taksit` : "Tek çekim"}</p></div>
                  <div><p className="text-neutral-500">Tutar</p><p className="text-white font-mono-ed">{fmtTRY(paymentInfo.payment.amount)}</p></div>
                  {paymentInfo.transactions?.[0]?.masked_card && <div><p className="text-neutral-500">Kart</p><p className="text-white font-mono-ed">{paymentInfo.transactions[0].masked_card}</p></div>}
                  {paymentInfo.transactions?.[0]?.transaction_id && <div className="col-span-2"><p className="text-neutral-500">Transaction ID</p><p className="text-white font-mono-ed text-[11px]">{paymentInfo.transactions[0].transaction_id}</p></div>}
                </div>
                {paymentInfo.transactions?.length > 0 && (
                  <div className="pt-2 border-t border-[#272B33] space-y-1">
                    {paymentInfo.transactions.map((t) => (
                      <p key={t.payment_transaction_id} className="text-[10px] text-neutral-500 font-mono-ed">
                        {t.status} · {fmtTRY(t.amount)} · {t.response_code || "—"} · {fmtDate(t.created_at)}
                      </p>
                    ))}
                  </div>
                )}
                {paymentInfo.payment.status === "SUCCESS" && (
                  <div className="flex gap-2 pt-2">
                    <button onClick={async () => {
                        if (!window.confirm("Ödeme bankadan iptal edilsin mi? Stok iade edilir.")) return;
                        try { await api.post(`/admin/orders/${detail.order_number}/payment/void`); toast.success("Ödeme iptal edildi"); openDetail(detail.order_number); load(); }
                        catch (e) { toast.error(errMsg(e)); }
                      }}
                      className="h-9 px-4 rounded-lg bg-rose-600/15 text-rose-400 text-xs font-semibold hover:bg-rose-600/25" data-testid="order-void-btn">
                      Ödemeyi İptal Et
                    </button>
                    <div className="flex gap-1.5 flex-1">
                      <input type="number" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={`İade tutarı (maks ${detail.total})`}
                        className="flex-1 h-9 px-3 bg-[#0F1115] border border-[#272B33] rounded-lg text-xs text-white focus:outline-none focus:border-blue-500" data-testid="order-refund-amount" />
                      <button onClick={async () => {
                          try { const r = await api.post(`/admin/orders/${detail.order_number}/payment/refund`, { amount: refundAmount ? parseFloat(refundAmount) : null }); toast.success(r.data.full ? "Tam iade yapıldı" : "Kısmi iade yapıldı"); openDetail(detail.order_number); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }}
                        className="h-9 px-4 rounded-lg bg-blue-600/15 text-blue-400 text-xs font-semibold hover:bg-blue-600/25" data-testid="order-refund-btn">
                        İade Et
                      </button>
                    </div>
                  </div>
                )}
                {paymentInfo.payment.status === "PARTIALLY_REFUNDED" && (
                  <p className="text-[11px] text-amber-400 pt-1">Kısmi iade yapıldı: {fmtTRY(paymentInfo.payment.refunded_amount)}</p>
                )}
                {["PENDING", "FAILED"].includes(paymentInfo.payment.status) && detail.payment_method === "havale" && (
                  <button onClick={async () => {
                      if (!window.confirm("Havale/EFT ödemesi alındı olarak onaylansın mı? Sipariş 'Ödeme Başarılı' olur ve fatura oluşturulur.")) return;
                      try { await api.put(`/admin/orders/${detail.order_number}/status`, { payment_status: "Başarılı" }); toast.success("Ödeme onaylandı"); openDetail(detail.order_number); load(); }
                      catch (e) { toast.error(errMsg(e)); }
                    }}
                    className="w-full h-9 rounded-lg bg-emerald-600/15 text-emerald-400 text-xs font-semibold hover:bg-emerald-600/25 mt-2" data-testid="order-havale-confirm">
                    Havale Ödemesini Onayla
                  </button>
                )}
              </div>
            )}

            <div className="bg-[#181B20] border border-[#272B33] rounded-xl p-4 space-y-3">
              <label className="block text-[11px] uppercase tracking-wider text-neutral-500">Sipariş Durumu</label>
              {detail.allowed_next?.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="order-quick-actions">
                  {detail.allowed_next.map((s) => (
                    <button key={s} onClick={() => updateStatus(detail.order_number, { status: s })}
                      className="h-9 px-4 rounded-xl bg-blue-600/15 text-blue-400 text-xs font-semibold hover:bg-blue-600/25"
                      data-testid={`order-action-${s}`}>{s} Yap</button>
                  ))}
                </div>
              )}
              {detail.allowed_next?.length === 0 && (
                <p className="text-xs text-neutral-500">Bu sipariş son durumda; yalnızca yetkili yönetici zorla geçiş yapabilir.</p>
              )}
              <select value={detail.status} onChange={(e) => updateStatus(detail.order_number, { status: e.target.value })} className={selectCls + " w-full"} data-testid="order-status-select">
                {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input value={cargo.company} onChange={(e) => setCargo({ ...cargo, company: e.target.value })} placeholder="Kargo firması" className={selectCls} data-testid="order-cargo-company" />
                <input value={cargo.tracking} onChange={(e) => setCargo({ ...cargo, tracking: e.target.value })} placeholder="Takip no" className={selectCls} data-testid="order-cargo-tracking" />
              </div>
              <button onClick={() => updateStatus(detail.order_number, { cargo_company: cargo.company, cargo_tracking_number: cargo.tracking })}
                className="w-full h-10 rounded-xl bg-blue-600/15 text-blue-400 text-sm font-semibold hover:bg-blue-600/25" data-testid="order-cargo-save">
                Kargo Bilgisini Kaydet
              </button>
            </div>

            {detail.timeline?.length > 0 && (
              <div className="bg-[#181B20] border border-[#272B33] rounded-xl p-4 mt-4" data-testid="order-timeline">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-3">Sipariş Zaman Çizelgesi</p>
                <div className="space-y-0">
                  {detail.timeline.map((ev, i) => (
                    <div key={ev.event_id} className="flex gap-3 relative">
                      {i < detail.timeline.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-[#272B33]" />}
                      <span className="w-[11px] h-[11px] rounded-full bg-blue-500 mt-1 shrink-0 relative z-10" />
                      <div className="pb-4">
                        <p className="text-xs text-white font-medium">{ev.description}</p>
                        <p className="text-[10px] text-neutral-500">{fmtDate(ev.created_at)} · {ev.actor_type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
